import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonService, DocumentService } from '../../../../../../shared/services';
import { of, forkJoin, Subscription } from 'rxjs';
import { RolePlayersCardViewComponent } from '../role-players-card-view/role-players-card-view.component';
import { ToastrService } from 'ngx-toastr';
import { catchError, take } from 'rxjs/operators';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'role-players-card-in-form',
  templateUrl: './role-players-card-in-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolePlayersCardInFormComponent implements OnInit, OnDestroy {
  @ViewChildren('dynamicRolePlayerRef') allRolePlayerComponents: QueryList<RolePlayersCardViewComponent>;
  @Input() mode;
  @Input() data;
  @Input() origin;
  @Input() formName;
  @Input() templateId;
  @Input() currentuserId;
  @Input() currentFormId;
  @Input() formsList = [];
  @Input() isRemoveEditSaveSharing;
  @Input() formsRolePlayers = [];
  @Input() aiIsProcessing = false;
  @Input() hideManageButton = false;
  @Output() rolePlayerSelectionChange = new EventEmitter<{
    rolePlayer: any;
    index: number;
    isChecked: boolean;
    category: string;
  }>();
  @Output() aiSuggestionSelectionChange = new EventEmitter<{
    suggestion: any;
    index: number;
    isChecked: boolean;
    category: string;
  }>();

  isDataLoading = true;
  rolePlayersJsonData = [];
  filteredFormRolePlayers = [];
  formParentTrustAndSubTrust = [];
  subscription = new Subscription();
  rolePlayersSuggestionsByCategory = {};

  constructor(
    private _commonService: CommonService,
    private _documentService: DocumentService,
    private toastr: ToastrService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const trustCategories = ['parent_trust', 'sub_trust'];
    this.formParentTrustAndSubTrust = this.formsRolePlayers.filter((cat) => trustCategories.includes(cat));
    this.filteredFormRolePlayers = this.formsRolePlayers.filter((cat) => !trustCategories.includes(cat));
    this.loadInitialData();
  }

  getVaultUserFormRolePlayersSuggestion() {
    return this._documentService.getVaultUserFormRolePlayersSuggestion(this.currentuserId, this.currentFormId).pipe(
      take(1),
      catchError((error) => {
        this.toastr.error('Error occurred while getting role players suggestions', error?.error?.message);
        return of({});
      })
    );
  }

  getRolePlayersJsonData() {
    return this._commonService.getRolePlayersJsonData().pipe(
      take(1),
      catchError((error) => {
        this.toastr.error('Error occurred while getting role players json data', error?.error?.message);
        return of([]);
      })
    );
  }

  loadInitialData() {
    this.subscription.add(
      forkJoin({
        rolePlayersJson: this.getRolePlayersJsonData(),
        rolePlayersSuggestions: this.getVaultUserFormRolePlayersSuggestion(),
      }).subscribe({
        next: (results) => {
          this.rolePlayersJsonData = [...results.rolePlayersJson];
          this.rolePlayersSuggestionsByCategory = results.rolePlayersSuggestions || {};
          this.isDataLoading = false;
          this.cd.detectChanges();
        },
      })
    );
  }

  refreshAISuggestions() {
    this.rolePlayersSuggestionsByCategory = {};
    this.cd.detectChanges();
    this.subscription.add(
      this.getVaultUserFormRolePlayersSuggestion().subscribe((suggestions) => {
        this.rolePlayersSuggestionsByCategory = suggestions || {};
        this.cd.detectChanges();
      })
    );
  }

  rolePlayersItemInSetUpList(event) {
    if (!this.rolePlayersJsonData || !event.list) {
      return;
    }

    const statusMap = new Map(event.list.map((obj) => [obj.sub_type, obj.status]));
    this.rolePlayersJsonData = this.rolePlayersJsonData.map((item) => {
      const newStatus = statusMap.get(item.role_player_category);
      return newStatus !== undefined ? { ...item, status: newStatus } : item;
    });

    this.cd.detectChanges();
  }

  openManageRolePlayersDialog(event) {
    const rolePlayerJsonData = this.rolePlayersJsonData.find((category) => {
      return category.role_player_category === event.sub_type;
    });
    if (!rolePlayerJsonData) return;
    const updatedRolePlayerJsonData = { ...rolePlayerJsonData, status: event.status };
    const targetComponent = this.allRolePlayerComponents.find((comp) => comp.category === event.sub_type);
    if (targetComponent) {
      targetComponent.openManageRolePlayersDialog(true, updatedRolePlayerJsonData);
    }
  }

  deleteRolePlayer(rolePlayer: any, category: string) {
    const targetComponent = this.allRolePlayerComponents.find((comp) => comp.category === category);
    if (targetComponent) {
      targetComponent.deleteVaultUserFormRolePlayers(rolePlayer, false);
    }
  }

  deleteRolePlayerAISuggestion(suggestion: any, category: string) {
    const targetComponent = this.allRolePlayerComponents.find((comp) => comp.category === category);
    if (targetComponent) {
      targetComponent.deleteVaultUserFormRolePlayersSuggestion(suggestion);
    }
  }

  onRolePlayerSelectionChange(event: { rolePlayer: any; index: number; isChecked: boolean; category: string }) {
    this.rolePlayerSelectionChange.emit(event);
    this.cd.detectChanges();
  }

  onAISuggestionSelectionChange(event: { suggestion: any; index: number; isChecked: boolean; category: string }) {
    this.aiSuggestionSelectionChange.emit(event);
    this.cd.detectChanges();
  }

  clearSelections() {
    if (this.allRolePlayerComponents) {
      this.allRolePlayerComponents.forEach((component) => {
        component.clearSelections();
      });
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
